import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import DropDown from "@components/ui/dropdown/DropDown";
import Button from "@components/ui/buttons/Button";
import { useState } from "react";
import TextField from "@components/ui/textfields/TextField";
import { PartnerInfos } from "types";
import { showNotification } from "context/notification.service";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";

const companyTypeOptions = [
  { value: "gros_oeuvre", label: "Gros œuvre" },
  { value: "charpente", label: "Charpente" },
  { value: "couverture", label: "Couverture" },
  { value: "menuiserie", label: "Menuiserie" },
  { value: "enduit", label: "Enduit" },
  { value: "cloisonnement", label: "Cloisonnement" },
  { value: "carrelage", label: "Carrelage / Faïence" },
  { value: "electricite", label: "Électricité" },
  { value: "plomberie", label: "Plomberie" },
  { value: "peinture", label: "Peinture" },
  { value: "parquet", label: "Parquet" },
  { value: "architecte", label: "Architecte" },
  { value: "bureau_d_etudes", label: "Bureau d'études" },
  { value: "jardinage", label: "Jardinage" },
  { value: "nettoyage", label: "Nettoyage" },
  { value: "demenagement", label: "Déménagement" },
  { value: "informatique", label: "Informatique" },
  { value: "autre_btp", label: "Autre BTP" },
  { value: "autre_service", label: "Autre service" },
  { value: "autre", label: "Autre" },
];

export const CreatePartnerModal = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave?: (partner: PartnerInfos) => void;
}) => {
  const addPartner = useProjectStore((state) => state.addPartner);
  const userData = useDataStore((state) => state.userData);
  const [partner, setPartner] = useState<PartnerInfos>({
    id: `partner_${Date.now()}`,
    display_name: "",
    role: "",
    company_type: "autre",
  });

  function handleSave() {
    if (!partner.display_name && !partner.company_name) {
      showNotification({
        type: "error",
        message: "Veuillez renseigner un nom ou une entreprise",
      });
      return;
    }
    addPartner(partner, userData?.org_id || "");
    onSave?.(partner);
    showNotification({
      type: "success",
      message: "Artisan ajouté avec succès",
    });
    onClose();
  }

  return (
    <BaseModal
      title="Nouvel intervenant"
      startIcon={<Icon1 name="intervenant" size={20} />}
      onClose={onClose}
    >
      <div className="bg-white flex flex-col gap-2 p-4">
        <div className="text-h3 text-neutral-400 font-semibold">Entreprise</div>
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Nom entreprise"
              placeholder="Construction ABC"
              value={partner.company_name || ""}
              onChange={(value) =>
                setPartner({ ...partner, company_name: value })
              }
            />
          </div>
          <div className="flex-1">
            <DropDown
              options={companyTypeOptions}
              label="Type"
              value={partner.company_type}
              onSelect={(value) =>
                setPartner({
                  ...partner,
                  company_type: value as PartnerInfos["company_type"],
                })
              }
            />
          </div>
        </div>
        <TextField
          label="Rôle / Fonction"
          placeholder="Architecte, Maçon, Électricien..."
          value={partner.role}
          onChange={(value) => setPartner({ ...partner, role: value })}
        />
        <div className="flex flex-row gap-2">
          <TextField
            label="Tel entreprise"
            placeholder="01 23 45 67 89"
            type="phone"
            value={partner.company_phone || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_phone: value })
            }
          />
          <TextField
            label="Email entreprise"
            placeholder="contact@entreprise.fr"
            value={partner.company_email || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_email: value })
            }
          />
        </div>
        <div className="flex flex-row gap-2">
          <TextField
            label="Adresse"
            placeholder="12 Rue de la Paix"
            value={partner.company_address || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_address: value })
            }
          />
          <TextField
            label="Code postal"
            placeholder="75002"
            value={partner.company_postal || ""}
            onChange={(value) =>
              setPartner({ ...partner, company_postal: value })
            }
          />
        </div>
        <TextField
          label="Ville"
          placeholder="Paris"
          value={partner.company_city || ""}
          onChange={(value) =>
            setPartner({ ...partner, company_city: value })
          }
        />

        <div className="border-t border-neutral-200 my-2" />
        <div className="text-h3 text-neutral-400 font-semibold">Contact</div>
        <TextField
          label="Nom complet"
          placeholder="Sophie Martin"
          value={partner.display_name}
          onChange={(value) =>
            setPartner({ ...partner, display_name: value })
          }
        />
        <div className="flex flex-row gap-2">
          <TextField
            label="Téléphone"
            placeholder="06 12 34 56 78"
            type="phone"
            value={partner.phone_number || ""}
            onChange={(value) =>
              setPartner({ ...partner, phone_number: value })
            }
          />
          <TextField
            label="Email"
            placeholder="sophie@martin.fr"
            value={partner.email || ""}
            onChange={(value) => setPartner({ ...partner, email: value })}
          />
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>
          Annuler
        </SecondaryButton>
        <Button
          variant="small"
          label="Enregistrer"
          onClick={handleSave}
        />
      </div>
    </BaseModal>
  );
};
